import React, { useEffect, useState } from 'react';
import { Button, Offcanvas, Spinner } from 'react-bootstrap';
import {
  FiAlertCircle,
  FiCalendar,
  FiClock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiVideo,
} from 'react-icons/fi';

import StatusBadge, { STATUS_META } from './StatusBadge';
import { updateAppointmentStatus } from '../../api/client';
import {
  formatCurrency,
  formatDateTime,
  formatRelative,
  initials,
  titleCase,
} from '../../utils/format';

const MODALITY_ICON = { video: FiVideo, 'in-person': FiMapPin, phone: FiPhone };

function Field({ label, children }) {
  return (
    <div className="mm-detail__field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * Slide-over showing the full record for one appointment, and the only place a
 * status can be changed.
 */
export default function AppointmentDetail({ appointment, onClose, onUpdated }) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  // Clear transient state whenever a different appointment is opened.
  useEffect(() => {
    setError(null);
    setSaving(null);
  }, [appointment?.id]);

  if (!appointment) return null;

  const a = appointment;
  const ModalityIcon = MODALITY_ICON[a.modality] ?? FiVideo;
  const isFuture = new Date(a.scheduledFor) > new Date();

  const changeStatus = async (status) => {
    setSaving(status);
    setError(null);
    try {
      const result = await updateAppointmentStatus(a.id, status);
      onUpdated(result.appointment);
    } catch (err) {
      // 409 means the change contradicts the appointment's own clock; the
      // server's message explains exactly why, so surface it verbatim.
      setError(
        err.body?.errors?.status ||
          err.message ||
          'Could not update the status. Please try again.'
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <Offcanvas
      show={Boolean(appointment)}
      onHide={onClose}
      placement="end"
      className="mm-detail"
      aria-labelledby="mm-detail-title"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title id="mm-detail-title">
          <span className="mm-detail__ref">{a.reference}</span>
          <StatusBadge status={a.status} />
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {/* -- Who is seeing whom: the question this dashboard exists to answer */}
        <div className="mm-detail__pair">
          <div className="mm-detail__party">
            <span className="mm-detail__party-label">Client</span>
            <div className="mm-person">
              <span className="mm-person__avatar" aria-hidden="true">
                {initials(a.customer.name)}
              </span>
              <span className="mm-person__text">
                <span className="mm-person__name">{a.customer.name}</span>
                <a
                  className="mm-person__sub mm-detail__mail"
                  href={`mailto:${a.customer.email}`}
                >
                  <FiMail size={11} aria-hidden="true" />
                  {a.customer.email}
                </a>
              </span>
            </div>
          </div>

          <span className="mm-detail__arrow" aria-hidden="true" />

          <div className="mm-detail__party">
            <span className="mm-detail__party-label">Therapist</span>
            <div className="mm-person">
              <img
                className="mm-person__photo"
                src={a.therapist.image}
                alt=""
                width={32}
                height={32}
              />
              <span className="mm-person__text">
                <span className="mm-person__name">{a.therapist.name}</span>
                <span className="mm-person__sub">{a.therapist.title}</span>
              </span>
            </div>
          </div>
        </div>

        <dl className="mm-detail__fields">
          <Field label="Scheduled for">
            <span className="d-flex align-items-center gap-2">
              <FiCalendar size={14} aria-hidden="true" />
              {formatDateTime(a.scheduledFor)}
            </span>
            <span className="mm-detail__hint">{formatRelative(a.scheduledFor)}</span>
          </Field>

          <Field label="Duration">
            <span className="d-flex align-items-center gap-2">
              <FiClock size={14} aria-hidden="true" />
              {a.durationMinutes} minutes
            </span>
          </Field>

          <Field label="Format">
            <span className="d-flex align-items-center gap-2">
              <ModalityIcon size={14} aria-hidden="true" />
              {titleCase(a.modality)}
            </span>
            {a.location && <span className="mm-detail__hint">{a.location}</span>}
          </Field>

          <Field label="Session type">{a.sessionType}</Field>

          <Field label="Fee">
            <strong>{formatCurrency(a.fee, a.currency)}</strong>
          </Field>

          <Field label="Booked on">{formatDateTime(a.createdAt)}</Field>

          {a.cancelReason && (
            <Field label="Cancellation reason">{a.cancelReason}</Field>
          )}

          {a.notes && <Field label="Notes">{a.notes}</Field>}
        </dl>

        {/* -- Status controls ------------------------------------------- */}
        <div className="mm-detail__actions">
          <h3 className="mm-detail__actions-title">Update status</h3>

          {error && (
            <div className="mm-form-alert mm-form-alert--error" role="alert">
              <FiAlertCircle size={15} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div className="mm-detail__buttons">
            {Object.keys(STATUS_META).map((status) => {
              const isCurrent = status === a.status;
              // The server rejects these for a future session; disabling them
              // here explains why before the click rather than after.
              const blocked =
                isFuture && (status === 'completed' || status === 'no-show');

              return (
                <Button
                  key={status}
                  variant="outline-ink"
                  size="sm"
                  className={isCurrent ? 'mm-detail__btn--current' : ''}
                  disabled={isCurrent || blocked || saving !== null}
                  title={
                    blocked
                      ? 'This session has not taken place yet'
                      : undefined
                  }
                  onClick={() => changeStatus(status)}
                >
                  {saving === status ? (
                    <Spinner animation="border" size="sm" aria-hidden="true" />
                  ) : (
                    STATUS_META[status].label
                  )}
                </Button>
              );
            })}
          </div>

          {isFuture && (
            <p className="mm-detail__hint mt-2 mb-0">
              This session is in the future, so it can&rsquo;t yet be marked
              completed or as a no-show.
            </p>
          )}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
